import { Copy, Zap } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ReceiptDetailRow } from '@/components/receipts/ReceiptDetailRow';
import { formatElectricityToken, formatReceiptCurrency, copyText } from '@/utils/receiptFormatters';
import type { BillReceiptData } from '@/types/receipt';
import { notify } from '@/utils/notify';

interface ElectricityReceiptProps {
  receipt: BillReceiptData;
  compact?: boolean;
}

export function ElectricityReceipt({ receipt, compact = false }: ElectricityReceiptProps) {
  const meterType = receipt.meterType?.toUpperCase() ?? 'PREPAID';
  const isPostpaid = meterType === 'POSTPAID';
  const formattedToken = receipt.token ? formatElectricityToken(receipt.token) : null;

  return (
    <div className="mx-auto w-full min-w-0 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-soft">
      <div className="bg-gradient-to-r from-secondary-500 to-primary px-4 py-3 text-white">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white/20">
              <img src="/assets/logo-dark.jpeg" alt="AS DataLink" className="h-full w-full object-contain p-1" />
            </div>
            <div className="min-w-0">
              <h2 className="truncate text-sm font-bold sm:text-base">AS DataLink</h2>
              <p className="text-[11px] opacity-90 sm:text-xs">Electricity Payment</p>
            </div>
          </div>
          <div className="shrink-0 text-right text-base font-bold sm:text-lg">
            {formatReceiptCurrency(receipt.amountPaid)}
          </div>
        </div>
      </div>

      <div className={`space-y-3 ${compact ? 'p-3' : 'p-4'}`}>
        <div className="flex justify-center">
          <div className="flex items-center gap-1.5 rounded-full bg-green-100 px-3 py-1 text-[11px] font-medium text-green-800 sm:text-xs">
            <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
            PAYMENT SUCCESSFUL
          </div>
        </div>

        {!isPostpaid && formattedToken && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5">
            <div className="mb-1.5 flex items-center justify-center gap-1.5">
              <Zap className="h-3.5 w-3.5 shrink-0 text-amber-700" />
              <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-800 sm:text-xs">
                Electricity Token
              </span>
            </div>
            <p className="break-all text-center font-mono text-sm font-bold leading-snug tracking-wide text-amber-950 sm:text-base">
              {formattedToken}
            </p>
            <Button
              variant="outline"
              size="sm"
              fullWidth
              className="mt-2"
              onClick={() =>
                void copyText(receipt.token!).then((ok) =>
                  ok ? notify.success('Token copied') : notify.error('Could not copy token'),
                )
              }
            >
              <Copy className="h-4 w-4" />
              Copy token
            </Button>
          </div>
        )}

        {isPostpaid && (
          <div className="rounded-xl border border-blue-100 bg-blue-50 px-3 py-2 text-center text-xs text-blue-900 sm:text-sm">
            Postpaid bill payment confirmed. No electricity token is issued — your account will be
            credited by the disco.
          </div>
        )}

        <div>
          <ReceiptDetailRow label="Provider" value={receipt.providerName} />
          <ReceiptDetailRow label="Meter Number" value={receipt.meterNumber ?? '—'} mono />
          <ReceiptDetailRow label="Meter Type" value={meterType} />
          {receipt.customerName && (
            <ReceiptDetailRow label="Customer Name" value={receipt.customerName} />
          )}
          {receipt.customerAddress && (
            <ReceiptDetailRow label="Address" value={receipt.customerAddress} />
          )}
          {!isPostpaid && (
            <ReceiptDetailRow label="Units Purchased" value={receipt.units ?? '—'} />
          )}
        </div>

        <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-0.5">
          <ReceiptDetailRow label="Transaction No." value={receipt.reference} mono />
          <ReceiptDetailRow label="Date & Time" value={`${receipt.date} at ${receipt.time}`} />
        </div>

        {!compact && (
          <div className="border-t border-gray-200 pt-3 text-center text-xs text-gray-500">
            <p className="font-medium">Thank you for using AS DataLink</p>
            <p className="mt-1">Digital Bill Payment Platform</p>
          </div>
        )}
      </div>
    </div>
  );
}
